import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { filterEmptyFilters } from '../helpers/helpers';
import { getSurveysList, ISurveyFilters } from '../services/surveys';
import { SurveyStatus } from './useStatus';

const ALLOWED_STATUS = new Set(Object.values(SurveyStatus));

const normalizeFilters = (obj: Record<string, string>): ISurveyFilters => {
  const out: Record<string, string> = { ...obj };

  if (out.status) {
    const cleaned = String(out.status)
      .split(',')
      .map(s => s.trim())
      .filter(s => ALLOWED_STATUS.has(s));

    if (cleaned.length) {
      out.status = Array.from(new Set(cleaned)).join(',');
    } else {
      delete out.status;
    }
  }

  return out as ISurveyFilters;
};

export const useSurveyTable = () => {
  const { search } = useLocation();

  const navigate = useNavigate();
  const getAppliedFilters = useCallback((): ISurveyFilters => {
    const raw = Object.fromEntries(new URLSearchParams(search)) as Record<string, string>;
    return normalizeFilters(raw);
  }, [search]);

  const applyFilters = useCallback(
    (newFilter: ISurveyFilters) => {
      const currentFilters = getAppliedFilters();
      const newFiltersState = { ...currentFilters, ...newFilter };

      const filteredFilters = filterEmptyFilters(newFiltersState);
      const newFiltersQuery = new URLSearchParams(filteredFilters).toString();

      navigate({ search: `?${newFiltersQuery}` });
    },
    [search, navigate]
  );

  const downloadSurveyList = async () => {
    const filters: ISurveyFilters = getAppliedFilters();
    const defaultFilters = { sortOrder: filters.sortOrder || 'endDate:desc' };

    if (!filters.perPage) {
      return await getSurveysList({ ...filters, ...defaultFilters, perPage: '10' });
    }
    return await getSurveysList({ ...filters, ...defaultFilters });
  };

  return {
    downloadSurveyList,
    applyFilters,
    getAppliedFilters,
  };
};
